import { BackTest } from 'components/common-form/sidePanel/backTest/BackTest';
import { connect } from 'react-redux';
import type { FormOwnProps } from 'shared/connect/connect';
import {
  cancelBackTest,
  removeSecondBackTestQuoteId,
  resetBackTest,
  runBackTest,
  setBackTestExpandedSection,
  setBackTestMaturitiesMode,
  setInitialBackTestQuoteLabel,
  setMaturities,
  setSecondBackTestQuoteId,
  setSecondBackTestQuoteLabel,
} from 'state/backTest/backTest.actions';
import type { BackTestFormError, BackTestModel, DateMode, ExpandedData } from 'state/backTest/backTest.model';
import {
  getBackTestByTabId,
  getBackTestMaturitiesValidationError,
  hasBackTestGlobalError,
  hasBackTestResult,
  hasErrorForInitialQuoteId,
  hasErrorForSecondQuoteId,
  hasRunBackTest,
  isBackTestRunning,
} from 'state/backTest/backTest.selector';
import { retryBackTest } from 'state/backTest/backTest.thunks';
import { getUserPreferencesBackTestPrecision } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';

export type StateProps = BackTestModel & {
  precision: number;
  isRunning: boolean;
  hasResult: boolean;
  hasError: boolean;
  hasErrorForSecondQuoteId: boolean;
  hasErrorForInitialQuoteId: boolean;
  hasRunBackTest: boolean;
  maturitiesValidationError: BackTestFormError | undefined;
};

export type OwnProps = FormOwnProps;

export type DispatchProps = {
  retryTest: () => void;
  addSecondQuoteId: () => void;
  setSecondQuoteId: (quoteId: string) => void;
  setInitialQuoteLabel: (quoteLabel?: string) => void;
  setSecondQuoteLabel: (quoteLabel?: string) => void;
  removeSecondQuoteId: () => void;
  runTest: () => void;
  cancelTest: () => void;
  resetTest: () => void;
  setMaturities: (maturities: readonly string[]) => void;
  setMaturityMode: (mode: DateMode) => void;
  setExpandedSection: (section: keyof ExpandedData, isExpanded: boolean) => void;
};

export type BackTestProps = StateProps & OwnProps & DispatchProps;
const mapStateToProps = (state: AppState, { formId }: OwnProps): StateProps => {
  const backTest = getBackTestByTabId(state, formId)!;
  const precision = getUserPreferencesBackTestPrecision(state)!;
  return {
    ...backTest,
    precision,
    hasErrorForInitialQuoteId: hasErrorForInitialQuoteId(state, formId),
    hasErrorForSecondQuoteId: hasErrorForSecondQuoteId(state, formId),
    hasError: hasBackTestGlobalError(state, formId),
    hasResult: hasBackTestResult(state, formId),
    isRunning: isBackTestRunning(state, formId),
    maturitiesValidationError: getBackTestMaturitiesValidationError(state, formId),
    hasRunBackTest: hasRunBackTest(state, formId),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { formId: tabId }: OwnProps): DispatchProps => ({
  addSecondQuoteId() {
    dispatch(setSecondBackTestQuoteId(tabId, null));
  },
  setSecondQuoteId(quoteId) {
    dispatch(setSecondBackTestQuoteId(tabId, quoteId));
  },
  setInitialQuoteLabel(quoteLabel) {
    dispatch(setInitialBackTestQuoteLabel(tabId, quoteLabel));
  },
  setSecondQuoteLabel(quoteLabel) {
    dispatch(setSecondBackTestQuoteLabel(tabId, quoteLabel));
  },
  removeSecondQuoteId() {
    dispatch(removeSecondBackTestQuoteId(tabId));
  },
  runTest() {
    dispatch(runBackTest(tabId));
  },
  cancelTest() {
    dispatch(cancelBackTest(tabId));
  },
  setMaturities(maturities) {
    dispatch(setMaturities(tabId, maturities));
  },
  setMaturityMode(mode) {
    dispatch(setBackTestMaturitiesMode(tabId, mode));
  },
  resetTest() {
    dispatch(resetBackTest(tabId));
  },
  setExpandedSection(section, isExpanded) {
    dispatch(setBackTestExpandedSection(tabId, section, isExpanded));
  },
  retryTest() {
    dispatch(retryBackTest(tabId));
  },
});

export const connectBackTest = connect(mapStateToProps, mapDispatchToProps);

export const ConnectedBackTest = connectBackTest(BackTest);
